import { Form, Input } from 'antd';
import { useState } from 'react';
import { GitLabApi } from 'osep-generator-service-client-v1';
import config from '../../../config';
import { useApiConfig } from '../../../hooks/useApiConfig';

import useOptions, {
  Option,
  PATH_NAME_PATTERN,
  PATH_NAME_PATTERN_INVALID_PROJECT_NAME_MESSAGE,
} from './useOptions';
import MobileSelection from '../MobileSelection';

const GITLAB_DEVELOPER_ACCESS_LEVEL = +config.gitlabDeveloperAccessLevel;

const GitLabProject: React.FC = () => {
  const {
    isLoading,
    availableOptions,
    selectedOptions,
    canAddNewGroup,
    allOptionsByIds,
    searchTerm = '',
    onSearch,
    value = [],
    onChange,
    searchTermError = null,
  } = useOptions();

  const { getApiConfig, axiosInstance } = useApiConfig();

  const [existingNewGroupIds, setExistingNewGroupIds] = useState<string[]>([]);
  const [existingNewGroupError, setExistingNewGroupError] = useState<
    string | null
  >(null);

  return (
    <>
      <Form.Item
        label="GitLab Group"
        name="gitlab_group"
        rules={[
          {
            required: true,
            message: 'Please select a GitLab group',
          },
          {
            validator: async (_, groupOptions: Option[] = []) => {
              // Only applies to the group itself, new groups below it are allowed
              const selectedGroup = groupOptions[groupOptions.length - 1];

              if (
                selectedGroup?.type === 'group' &&
                selectedGroup.projectCreationForbidden
              ) {
                return Promise.reject(
                  `Projects cannot be created directly in group "${groupOptions
                    .map((o) => o.label)
                    .join('/')}", please select or create a subgroup`,
                );
              }
            },
          },
          {
            validator: async (_, groupOptions: Option[] = []) => {
              // A new group that already exists is not listed, e.g. because it is excluded, so it cannot be used.
              // Only the first new group can exist, all groups below it are new as well.
              const firstNewGroupIndex = groupOptions.findIndex(
                (o) => o?.type === 'new-group',
              );

              if (firstNewGroupIndex === -1) {
                setExistingNewGroupIds([]);
                setExistingNewGroupError(null);
                return;
              }

              const firstNewGroup = groupOptions[firstNewGroupIndex];

              const newGroupFullPath = groupOptions
                .slice(0, firstNewGroupIndex + 1)
                .map((o) => o.label)
                .join('/');

              const config = await getApiConfig();
              const gitLabApi = new GitLabApi(config, undefined, axiosInstance);

              const response = await gitLabApi.getGitlabPathStatus(
                encodeURIComponent(newGroupFullPath),
              );

              const exists = response.data !== 'NOT_EXISTING';
              const error = exists
                ? `Project creation is not allowed in "${newGroupFullPath}" or its subgroups`
                : null;
              setExistingNewGroupIds(exists ? [firstNewGroup.value] : []);
              setExistingNewGroupError(error);

              if (error) {
                return Promise.reject(error);
              }
            },
          },
          {
            validateTrigger: ['onSubmit'],
            validator: async () => {
              const parentGroup = selectedOptions
                .slice()
                .reverse()
                .find((o) => o.type === 'group');

              if (!parentGroup) {
                return;
              }

              const config = await getApiConfig();
              const gitLabApi = new GitLabApi(config, undefined, axiosInstance);

              return gitLabApi
                .getGitlabGroupsMembership(parentGroup?.value)
                .then((response) => {
                  if (
                    response.data.accessLevel < GITLAB_DEVELOPER_ACCESS_LEVEL
                  ) {
                    return Promise.reject(
                      `At least developer access required for group "${selectedOptions
                        .filter((o) => o.type === 'group')
                        .map((o) => o.label)
                        .join('/')}"`,
                    );
                  }
                });
            },
          },
        ]}
        normalize={(value: string[]) => {
          return value.map((v) => allOptionsByIds[v]);
        }}
      >
        <MobileSelection
          isLoading={isLoading}
          availableOptions={availableOptions}
          selectedOptions={selectedOptions}
          canAddNewItem={canAddNewGroup}
          allOptionsByIds={allOptionsByIds}
          searchTerm={searchTerm}
          onSearch={onSearch}
          value={value}
          onChange={onChange}
          searchTermError={searchTermError}
          dropdownWarning={existingNewGroupError}
          placeholder=""
          newItemLabel="New"
          notFoundContentDescription="No groups"
          showNewItem={true}
          invalidItemIds={existingNewGroupIds}
        />
      </Form.Item>
      <Form.Item
        label="GitLab Project"
        name="gitlab_project"
        rules={[
          {
            required: true,
            message: 'Please enter a GitLab project name',
          },
          {
            pattern: PATH_NAME_PATTERN,
            message: PATH_NAME_PATTERN_INVALID_PROJECT_NAME_MESSAGE,
          },
          {
            validateTrigger: ['onSubmit'],
            validator: async (_, gitlab_project?: string) => {
              if (!gitlab_project) {
                return;
              }

              const config = await getApiConfig();
              const gitLabApi = new GitLabApi(config, undefined, axiosInstance);

              const projectFullPath = [
                ...value.map((v) => allOptionsByIds[v]?.label || ''),
                gitlab_project,
              ].join('/');

              const response = await gitLabApi.getGitlabPathStatus(
                encodeURIComponent(projectFullPath),
              );

              if (response.data === 'PROJECT_EXISTING') {
                return Promise.reject(
                  `Project "${projectFullPath}" already exists`,
                );
              }

              if (response.data === 'GROUP_EXISTING') {
                return Promise.reject(
                  `Group "${projectFullPath}" already exists`,
                );
              }
            },
          },
        ]}
      >
        <Input />
      </Form.Item>
    </>
  );
};

export default GitLabProject;
